import { Pipe, PipeTransform } from '@angular/core';

/** A piece of one of Ezri's replies: plain text, or a link to show as one. */
export interface EzriTextPart {
  text: string;
  href?: string;
}

/**
 * The sites Ezri's industry knowledge cites (Services/Assistant/Industry): only these
 * become links. A reply is model output, and a client's name or a note can steer it,
 * so any other address stays plain text the user can read but not click.
 */
const OFFICIAL_HOSTS = ['gov.il', 'nevo.co.il', 'kolzchut.org.il'];

// [name](https://...) as the model sometimes writes it, or a bare https address.
// A bare one ends before trailing punctuation: "(see https://x.gov.il)." links x.gov.il.
const LINK = /\[([^\]\n]{1,200})\]\((https:\/\/[^\s)]+)\)|(https:\/\/[^\s<>()[\]]*[^\s<>()[\].,;:!?'"״])/g;

export function isOfficialLink(url: string): boolean {
  try {
    const { protocol, hostname } = new URL(url);
    return protocol === 'https:' && OFFICIAL_HOSTS.some(h => hostname === h || hostname.endsWith('.' + h));
  } catch {
    return false;
  }
}

/** Splits a reply into text and links; a link to anything but an official site stays text, as written. */
export function ezriTextParts(text: string): EzriTextPart[] {
  const parts: EzriTextPart[] = [];
  let last = 0;
  for (const match of text.matchAll(LINK)) {
    const [whole, name, markdownUrl, bareUrl] = match;
    const url = markdownUrl ?? bareUrl;
    if (!isOfficialLink(url)) continue;
    if (match.index > last) parts.push({ text: text.slice(last, match.index) });
    parts.push({ text: name ?? decodeForDisplay(url), href: url });
    last = match.index + whole.length;
  }
  if (last < text.length) parts.push({ text: text.slice(last) });
  return parts;
}

/** Kol Zchut's addresses carry Hebrew page names: show them as letters, not %D7%A2... */
function decodeForDisplay(url: string): string {
  try {
    return decodeURI(url);
  } catch {
    return url;
  }
}

@Pipe({ name: 'ezriTextParts', standalone: true })
export class EzriTextPartsPipe implements PipeTransform {
  transform(text: string): EzriTextPart[] {
    return ezriTextParts(text ?? '');
  }
}
